/**
 * TG-6 (AC-15): the field-key conventions for one exposure section
 * instance's answers record, shared between the rendering engine
 * (`ExposureQuestionsEngine.tsx`), the validator (`exposure-validation.ts`)
 * and `state/section-instances.ts`'s own `additionalDetailsRules` reader
 * (TG-6a, already built) — every repeat-block field's stored value is
 * `readonly { key: string; outcome: string }[]`, exactly the shape that
 * module's own doc comment on `AdditionalDetailsRule` declares, so this
 * task group needs no change there.
 */

/** The on-file item a picker field picked — `{screenKey, epicRecordId}`
 * (AC-5, m1-exposure-detail-sourcing TG-5), so the Sold/Replaced/Removed
 * flag (matched back against `OnFileScreenItem.epicRecordId`) and the
 * picker's own cross-block exclusion both survive resume: both are
 * computed by matching THIS shape against the on-file read's own items,
 * never by re-deriving an identity from the picker field's plain string
 * value alone. */
export interface PickedOnFileItem {
  readonly screenKey: string;
  readonly epicRecordId: number;
}

export interface RepeatItemAnswer {
  readonly key: string;
  /** `""` for a repeat block with no outcome radio (e.g. Business
   * operations Q3's "new construction" blocks). */
  readonly outcome: string;
  readonly fields: Readonly<Record<string, string>>;
  /** Keyed by the picker field's own `RepeatFieldSpec.id` (on-file-fields.ts's
   * `OnFilePickerSpec.fieldId`) — a repeat item with no picker field, or
   * one where nothing has been picked yet, carries this as `{}` or omits
   * a given field id entirely. Optional so every existing reader of this
   * type (e.g. `state/section-instances.ts`'s own `isRepeatBlockItem`,
   * which checks only `key`/`outcome`) keeps working unchanged against a
   * stored item that does carry it — an additional, ignored property. */
  readonly pickedOnFileItems?: Readonly<Record<string, PickedOnFileItem>>;
}

export type ExposureAnswers = Readonly<Record<string, unknown>>;

export function yesNoDetailAnswerKey(fieldId: string): string {
  return fieldId;
}

export function detailTextKey(fieldId: string): string {
  return `${fieldId}__detail`;
}

export function repeatAnswerKey(fieldId: string): string {
  return `${fieldId}__answer`;
}

/** The repeat block's ITEMS live directly under `fieldId` itself (not a
 * derived key) — this is the exact shape `state/section-instances.ts`'s
 * `readRepeatBlockItems` already reads. */
export function repeatItemsKey(fieldId: string): string {
  return fieldId;
}

export function checklistTickedKey(fieldId: string): string {
  return fieldId;
}

export function checklistOtherTextKey(fieldId: string): string {
  return `${fieldId}__otherText`;
}

export function readString(answers: ExposureAnswers, key: string): string {
  const value = answers[key];
  return typeof value === "string" ? value : "";
}

export function readStringArray(answers: ExposureAnswers, key: string): readonly string[] {
  const value = answers[key];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function isRepeatItemAnswer(value: unknown): value is RepeatItemAnswer {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { key?: unknown }).key === "string" &&
    typeof (value as { outcome?: unknown }).outcome === "string" &&
    typeof (value as { fields?: unknown }).fields === "object"
  );
}

export function readRepeatItems(answers: ExposureAnswers, fieldId: string): readonly RepeatItemAnswer[] {
  const value = answers[repeatItemsKey(fieldId)];
  return Array.isArray(value) ? value.filter(isRepeatItemAnswer) : [];
}

let nextRepeatItemKeySuffix = 0;

/** A stable per-item key, unique within this module's lifetime — repeat
 * items are added/removed in a React list and need a key React (and
 * `state/section-instances.ts`'s own additional-details-page id
 * encoding) can rely on never colliding within one section instance. */
export function createRepeatItemKey(): string {
  nextRepeatItemKeySuffix += 1;
  return `item-${Date.now().toString(36)}-${nextRepeatItemKeySuffix}`;
}

/** The on-file item picked for `pickerFieldId` on one repeat item, or
 * `undefined` when nothing has been picked there yet. */
export function pickedOnFileItemFor(item: RepeatItemAnswer, pickerFieldId: string): PickedOnFileItem | undefined {
  return item.pickedOnFileItems?.[pickerFieldId];
}

/** Records a picker field's pick on one repeat item: the picked item's
 * identity (`{screenKey, epicRecordId}`, surviving resume per AC-5) AND,
 * for `Select`'s own string-keyed binding, the SAME identity encoded as
 * that field's plain string value — the two are written together so
 * `item.fields[pickerFieldId]` and `item.pickedOnFileItems[pickerFieldId]`
 * never disagree about which item is picked. */
export function withPickedOnFileItem(
  item: RepeatItemAnswer,
  pickerFieldId: string,
  picked: PickedOnFileItem,
): RepeatItemAnswer {
  return {
    ...item,
    fields: { ...item.fields, [pickerFieldId]: String(picked.epicRecordId) },
    pickedOnFileItems: { ...item.pickedOnFileItems, [pickerFieldId]: picked },
  };
}

/** H2 `checkYear`: a ticked option is stored as `<fieldId>.<option>` holding its year (`""`
 * until entered); an unticked option has no key. */
export function checkYearKey(fieldId: string, option: string): string {
  return `${fieldId}.${option}`;
}

/** The options ticked for a `checkYear` field, each with its year, in stored order. */
export function readCheckYear(item: RepeatItemAnswer, fieldId: string): ReadonlyMap<string, string> {
  const prefix = `${fieldId}.`;
  return new Map(
    Object.entries(item.fields)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, year]) => [key.slice(prefix.length), year]),
  );
}

/** Whether `key` holds part of `fieldId`'s answer: the field itself, or one of its `checkYear` options. */
export function isFieldAnswerKey(key: string, fieldId: string): boolean {
  return key === fieldId || key.startsWith(`${fieldId}.`);
}

/** One subject of insurance added to a limits block, with an updated limit per valuation basis. */
export interface LimitsSubjectAnswer {
  /** The on-file subject's `epicRecordId`, or the fixed "other" subject's value. */
  readonly subject: string;
  readonly label: string;
  readonly values: Readonly<Record<string, string>>;
}

/** One property block of a limits question (H2 `limitProperty` with its `limitEntries`). */
export interface LimitsBlockAnswer {
  readonly key: string;
  /** The picked building's `epicRecordId`, `""` until one is picked. */
  readonly property: string;
  readonly pickedOnFileItem?: PickedOnFileItem;
  readonly subjects: readonly LimitsSubjectAnswer[];
}

function isLimitsBlockAnswer(value: unknown): value is LimitsBlockAnswer {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { key?: unknown }).key === "string" &&
    typeof (value as { property?: unknown }).property === "string" &&
    Array.isArray((value as { subjects?: unknown }).subjects)
  );
}

/** A limits question's blocks; one empty block once "Yes" is answered. */
export function readLimitsBlocks(answers: ExposureAnswers, fieldId: string): readonly LimitsBlockAnswer[] {
  const value = answers[fieldId];
  return Array.isArray(value) ? value.filter(isLimitsBlockAnswer) : [];
}
